import axios from '../helpers/axios';
import type { Action } from '../typings/reducers';

type AuthState = {
  authenticated: boolean;
  mailsuite: Record<string, unknown>;
  defaultUserBrand: Record<string, unknown>;
  upgradeAccountIsOpen: boolean;
  upgradeSlideshowAccountIsOpen: boolean;
  maintenance_mode: string;
  // Raw value from GET /api/me — undefined for users whose document
  // predates the field; normalize via normalizeUploadEndpoint on read.
  uploadEndpoint?: string | null;
  // When true, uploads run sequentially (concurrency 1) instead of in parallel —
  // a fallback for users with bad/unstable internet. Undefined for older docs.
  sequentialUploads?: boolean;
  // Server-driven feature toggles from GET /api/me. Absent for users whose
  // document predates the field, so every flag read must default to false.
  featureFlags?: {
    newDesign?: boolean;
    [key: string]: boolean | undefined;
  };
  taxSettings?: {
    errorMessage?: string;
    [key: string]: any;
  };
  klickTippTags?: any[];
  klickTipp?: any;
  strategySteps?: any[];
  digistore?: {
    discountKey?: string;
    [key: string]: any;
  };
  registrationProcess?: Record<string, any>;
  [key: string]: any;
};

const initialState: AuthState = {
  authenticated: false,
  mailsuite: {},
  defaultUserBrand: {},
  upgradeAccountIsOpen: false,
  upgradeSlideshowAccountIsOpen: false,
  maintenance_mode: ''
};

export default function (state: AuthState = initialState, action: Action): AuthState {
  switch (action.type) {
    case 'SET_MAINTENANCE_MODE':
      return { ...state, maintenance_mode: action.payload || '' };
    case 'SET_UPGRADE_ACCOUNT_OPEN_STATE':
      if (action.payload?.body) {
        axios.post('/api/useractivitylog/logaction', {
          action: 'SHOW_UPGRADE_MODAL',
          logAsHubpostNote: true,
          leadScore: 5
        });

        if (action.payload?.callback) {
          action.payload.callback();
        }
      }

      return { ...state, upgradeAccountIsOpen: action.payload.body };
    case 'SET_UPGRADE_SLIDESHOW_ACCOUNT_OPEN_STATE':
      return { ...state, upgradeSlideshowAccountIsOpen: action.payload };
    case 'AUTH_USER':
      return { ...state, authenticated: true };
    case 'UNAUTH_USER':
      return { ...state, authenticated: false };
    case 'SET_USER_VALUE':
      return { ...state, [action.payload.key]: [action.payload.value] };
    case 'SET_USER': {
      if (typeof action.payload.languageSoftware === 'number') {
        if (action.payload.languageSoftware && action.payload.languageSoftware >= 0) {
          window.LANGUAGE_SW = action.payload.languageSoftware;
        } else {
          window.LANGUAGE_SW = 0;
        }
      }

      if (action.payload.type) {
        window.USER_TYPE = action.payload.type;
        window.setUpMetadataByUserType(action.payload.type);
      }

      return {
        ...state,
        ...action.payload,
        taxSettings: {
          ...state.taxSettings,
          ...action.payload.taxSettings,
          errorMessage: state.taxSettings?.errorMessage
        }
      };
    }
    case 'SET_AUTHKEY':
      return { ...state, authKey: action.payload };
    case 'SET_RECEIPT_URL':
      return { ...state, receipt_url: action.payload };
    case 'USER_ADD_KT_TAG':
      return {
        ...state,
        klickTippTags: [...(state.klickTippTags || []), action.payload]
      };
    case 'USER_SET_KLICKTIPP':
      return { ...state, klickTipp: action.payload };
    case 'USER_ADD_STRATEGY_STEP':
      return {
        ...state,
        strategySteps: [...(state.strategySteps || []), action.payload]
      };
    case 'SET_USER_DIGISTORE':
      return { ...state, digistore: action.payload };
    case 'SET_USER_DIGISTORE_DISCOUNT_KEY':
      return {
        ...state,
        digistore: { ...(state.digistore || {}), discountKey: action.payload }
      };
    case 'USER_SET_STRATEGY_STEPS':
      return {
        ...state,
        strategySteps: action.payload
      };
    case 'set_registration_process':
      return {
        ...state,
        registrationProcess: {
          ...(state.registrationProcess || {}),
          [action.payload.key]: action.payload.value
        }
      };
    case 'SET_USER_TAX_SETTINGS':
      return {
        ...state,
        taxSettings: {
          ...(state.taxSettings || {}),
          ...action.payload
        }
      };
    case 'SET_UPLOAD_ENDPOINT':
      return { ...state, uploadEndpoint: action.payload };
    case 'SET_SEQUENTIAL_UPLOADS':
      return { ...state, sequentialUploads: action.payload };
    default:
      return state;
  }
}
